var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import { merge, wait } from '@streamr/utils';
import { scoped, Lifecycle, inject } from 'tsyringe';
import { ConfigInjectionToken } from './ConfigTypes';
import { DestroySignal } from './DestroySignal';
import { StreamrClientEventEmitter } from './events';
import { NetworkNodeFacade } from './NetworkNodeFacade';
import { Publisher } from './publish/Publisher';
import { pOnce } from './utils/promises';
export const DEFAULTS = {
    periods: [
        {
            duration: 60000,
            streamId: 'streamr.eth/metrics/nodes/firehose/min'
        },
        {
            duration: 3600000,
            streamId: 'streamr.eth/metrics/nodes/firehose/hour'
        },
        {
            duration: 86400000,
            streamId: 'streamr.eth/metrics/nodes/firehose/day'
        }
    ],
    maxPublishDelay: 30000
};
const getNormalizedConfig = (config) => {
    if (config.metrics === true) {
        return DEFAULTS;
    }
    else if (config.metrics === false) {
        return {
            ...DEFAULTS,
            periods: []
        };
    }
    else if (config.metrics !== undefined) {
        return merge(DEFAULTS, config.metrics);
    }
    else {
        const isEthereumAuth = (config.auth?.ethereum !== undefined);
        return {
            ...DEFAULTS,
            periods: isEthereumAuth ? [] : DEFAULTS.periods
        };
    }
};
let MetricsPublisher = class MetricsPublisher {
    publisher;
    node;
    config;
    eventEmitter;
    destroySignal;
    constructor(publisher, node, config, eventEmitter, destroySignal) {
        this.publisher = publisher;
        this.node = node;
        this.config = getNormalizedConfig(config);
        this.eventEmitter = eventEmitter;
        this.destroySignal = destroySignal;
        const ensureStarted = pOnce(async () => {
            const metricsContext = await this.node.getMetricsContext();
            const nodeId = await this.node.getNodeId();
            this.config.periods.forEach((config) => {
                metricsContext.createReportProducer(async (report) => {
                    await this.publish(report, config.streamId, nodeId);
                }, config.duration, this.destroySignal.abortSignal);
            });
        });
        if (this.config.periods.length > 0) {
            this.eventEmitter.on('messagePublished', () => ensureStarted());
            this.eventEmitter.on('streamPartSubscribed', () => ensureStarted());
        }
    }
    async publish(report, streamId, nodeId) {
        await wait(Math.random() * this.config.maxPublishDelay);
        const message = {
            ...report,
            node: {
                ...report.node,
                id: nodeId
            }
        };
        try {
            await this.publisher.publish(streamId, message, {
                timestamp: report.period.end,
                partitionKey: nodeId
            });
        }
        catch (e) {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            console.warn(`Unable to publish metrics: ${e.message}`);
        }
    }
};
MetricsPublisher = __decorate([
    scoped(Lifecycle.ContainerScoped),
    __param(2, inject(ConfigInjectionToken)),
    __metadata("design:paramtypes", [Publisher,
        NetworkNodeFacade, Object, StreamrClientEventEmitter,
        DestroySignal])
], MetricsPublisher);
export { MetricsPublisher };
